/**
 * Minimal SHA-1 hex digest for deterministic ID hashing (e.g. Lambplan).
 * Not for password/crypto use — sync, ASCII-oriented, no deps.
 */
export function sha1Hex(message: string): string {
  const bytes = utf8Bytes(message);
  const words = bytesToWords(bytes);
  const bitLen = bytes.length * 8;

  const padIndex = bitLen >> 5;
  words[padIndex] = (words[padIndex] ?? 0) | (0x80 << (24 - (bitLen % 32)));
  words[(((bitLen + 64) >> 9) << 4) + 15] = bitLen;

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;

  const w = new Array<number>(80);
  for (let i = 0; i < words.length; i += 16) {
    for (let t = 0; t < 16; t++) w[t] = words[i + t] ?? 0;
    for (let t = 16; t < 80; t++) {
      const x =
        (w[t - 3] ?? 0) ^ (w[t - 8] ?? 0) ^ (w[t - 14] ?? 0) ^ (w[t - 16] ?? 0);
      w[t] = rotl(x, 1);
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;

    for (let t = 0; t < 80; t++) {
      const { f, k } = sha1Round(t, b, c, d);
      const temp = (rotl(a, 5) + f + e + k + (w[t] ?? 0)) | 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }

    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
  }

  return (
    wordToHex(h0) +
    wordToHex(h1) +
    wordToHex(h2) +
    wordToHex(h3) +
    wordToHex(h4)
  );
}

function sha1Round(
  t: number,
  b: number,
  c: number,
  d: number,
): { f: number; k: number } {
  if (t < 20) return { f: (b & c) | (~b & d), k: 0x5a827999 };
  if (t < 40) return { f: b ^ c ^ d, k: 0x6ed9eba1 };
  if (t < 60) return { f: (b & c) | (b & d) | (c & d), k: 0x8f1bbcdc };
  return { f: b ^ c ^ d, k: 0xca62c1d6 };
}

function rotl(n: number, s: number): number {
  return (n << s) | (n >>> (32 - s));
}

function wordToHex(n: number): string {
  let s = '';
  for (let i = 7; i >= 0; i--) {
    s += ((n >>> (i * 4)) & 0xf).toString(16);
  }
  return s;
}

function utf8Bytes(str: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < str.length; i++) {
    let c = str.charCodeAt(i);
    if (c < 0x80) {
      out.push(c);
    } else if (c < 0x800) {
      out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else if (c >= 0xd800 && c <= 0xdbff) {
      const next = str.charCodeAt(++i);
      c = 0x10000 + (((c & 0x3ff) << 10) | (next & 0x3ff));
      out.push(
        0xf0 | (c >> 18),
        0x80 | ((c >> 12) & 0x3f),
        0x80 | ((c >> 6) & 0x3f),
        0x80 | (c & 0x3f),
      );
    } else {
      out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }
  return out;
}

function bytesToWords(bytes: number[]): number[] {
  const words: number[] = [];
  for (let i = 0; i < bytes.length; i++) {
    const wordIndex = i >> 2;
    words[wordIndex] =
      (words[wordIndex] ?? 0) | ((bytes[i] ?? 0) << (24 - (i % 4) * 8));
  }
  return words;
}
